import React, { memo, useCallback, useMemo } from 'react';

import PageBlock from '@sharedCR/common/PageBlock';
import { EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { StatusEnum } from 'constants/status';
import usePopover from 'hooks/usePopover';

import { ConstructionTestIds } from '../../Construction.testIds';
import { formatMoney } from '../../Construction.utils';
import InlineNotice from '../InlineNotice';

import { getBudgetTableConfig } from './BudgetTableSection.config';
import { getBudgetTableData, getOverBudgetLine } from './BudgetTableSection.utils';

const BudgetTableSection = ({ budgetLines, isEditAllowed, isLoading, onEditBudget }) => {
  const editMenu = usePopover();

  const config = useMemo(() => getBudgetTableConfig(), []);
  const data = useMemo(() => getBudgetTableData(budgetLines), [budgetLines]);
  const overBudgetLine = useMemo(() => getOverBudgetLine(budgetLines), [budgetLines]);

  const onEditClick = useCallback(() => {
    editMenu.onClearAnchorElement();
    onEditBudget();
  }, [editMenu, onEditBudget]);

  const menuItems = useMemo(
    () => [
      {
        text: 'Edit Construction Budget',
        onClick: onEditClick,
        icon: <EditPenIcon size={IconSize[20]} />,
        isDisabled: !isEditAllowed,
      },
    ],
    [onEditClick, isEditAllowed],
  );

  return (
    <PageBlock
      withAction
      data-testid={ConstructionTestIds.BUDGET_SECTION}
      isActionDisabled={!isEditAllowed}
      isMenuOpened={!!editMenu.anchorEl}
      title="Construction Budget"
      onMenuClick={editMenu.onSetAnchorElement}
    >
      <MenuPopoverCDS
        anchorEl={editMenu.anchorEl}
        items={menuItems}
        onClose={editMenu.onClearAnchorElement}
      />
      {overBudgetLine && (
        <InlineNotice
          dataTestId={ConstructionTestIds.BUDGET_OVER_BUDGET_ALERT}
          variant={StatusEnum.WARNING}
        >
          <strong>{overBudgetLine.description}</strong>
          {' is over budget by '}
          <strong>{formatMoney(Math.abs(overBudgetLine.remaining))}</strong>. Review before
          approving the current draw, or add a change order against Contingency.
        </InlineNotice>
      )}
      <TableCDS config={config} data={data} isLoading={isLoading} title="" />
    </PageBlock>
  );
};

BudgetTableSection.propTypes = {
  onEditBudget: PropTypes.func.isRequired,
  isEditAllowed: PropTypes.bool,
  isLoading: PropTypes.bool,
  budgetLines: PropTypes.array,
};

export default memo(BudgetTableSection);
